import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import type Company from '../../../../models/company.js';
import type { LedgerBalanceReading } from '../../../../models/company.js';
import definition from '@job-schemas/public-ledger/platform-top-up.json';
import { jobSchema } from '../../job-schema.js';

import { createLogger } from '../../../../ns-services/logger.js';
import { formatCredits } from '../../../../helpers/public-ledger-credits.js';
import { ledgerExplorerUrl, ledgerFaucetUrl, ledgerNetworkLabel } from '../../../../helpers/public-ledger-network.js';
import {
  confirmedComplete,
  confirmRefusal,
  parseEnteredTotal,
  platformReadingFromAnswer,
  feesWaitingLine,
  platformTopUpFigures,
  platformTopUpIntro,
  type PlatformTopUpFigures,
} from '../../../../helpers/platform-top-up.js';
import {
  confirmPlatformTopUp,
  lambdaRefusal,
  readLedgerBalance,
} from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import '../../../../ns-elements/ns-input-field.js';
import '../../../../ns-elements/ns-ledger-balance.js';

const logger = createLogger('ns-public-ledger-platform-top-up');

const credits = (value: number | null) => (value === null ? undefined : `${formatCredits(value)} credits`);

/**
 * The PUBLIC_LEDGER team's urgent card: nodeStream's own public ledger account is low. The team sends funds to it outside
 * nodeStream, then enters the account's new total; public-ledger-confirm-platform-top-up checks it against the ledger and
 * completes the job only once nothing is owed and the account has reached its target.
 */
@customElement(NSPublicLedgerPlatformTopUp.is)
export default class NSPublicLedgerPlatformTopUp extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-platform-top-up';
  }

  static get schema(): JobSchema {
    // The job is completed by the confirm lambda, never by an outcome button
    return jobSchema(definition, { outcomes: { 'topped-up': { type: 'hidden' } }, icon: 'transaction' });
  }

  static styles = [
    ...super.styles,
    css`
      .primary-info ol {
        margin: 0 0 var(--wa-space-m);
        padding-inline-start: var(--wa-space-l);
        font-weight: 300;
      }

      .primary-info li + li {
        margin-top: var(--wa-space-2xs);
      }

      .primary-info ns-detail-list {
        margin: 0 0 var(--wa-space-m);
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __company?: Company;

  // The reading read when the card opened, until the stored one catches up
  @state()
  private __readNow: LedgerBalanceReading | null = null;

  @state()
  private __entered = '';

  @state()
  private __inputError?: string;

  @state()
  private __submitting = false;

  async connectedCallback() {
    await super.connectedCallback();
    try {
      this.__company = await this.loadJobCompany<Company>();
    } catch (err) {
      logger.warn('Unable to load the nodeStream company', err);
    }
    await this.__readBalance();
  }

  private get __figures(): PlatformTopUpFigures {
    const job = this._job;
    const network = platformTopUpFigures(job?.metadata, null).network;
    return platformTopUpFigures(job?.metadata, this.__reading(network));
  }

  // The newest of the reading read on open and the one stored on the nodeStream company
  private __reading(network: PlatformTopUpFigures['network']): LedgerBalanceReading | null {
    const stored = network ? (this.__company?.ledgerCred?.balances?.platform?.[network] ?? null) : null;
    const readNow = this.__readNow;
    if (!readNow) return stored;
    if (!stored?.readAt || (readNow.readAt && readNow.readAt >= stored.readAt)) return { ...stored, ...readNow };
    return stored;
  }

  // An external top-up is picked up when the card is opened: the lambda reads the account and has it stored
  private async __readBalance(fresh = false) {
    const companyId = this._job?.companyId;
    const network = platformTopUpFigures(this._job?.metadata, null).network;
    if (!companyId || !this._auth) return;
    try {
      const answer = await readLedgerBalance(this._auth.token, { companyId, roles: ['PLATFORM'], fresh });
      this.__readNow = platformReadingFromAnswer(answer, network);
    } catch (err) {
      logger.warn('Unable to read the platform balance', err);
    }
  }

  private async __confirm() {
    if (this.__submitting || !this._job) return;
    this.clearError();
    const entered = parseEnteredTotal(this.__entered);
    this.__inputError = entered.error;
    if (entered.credits === undefined) return;

    this.__submitting = true;
    this.loading('Checking the public ledger');
    try {
      const answer = await confirmPlatformTopUp(this._auth?.token, {
        jobId: this._job.id,
        enteredTotalCredits: entered.credits,
      });
      const job = await this._db?.getById<Job>('job', this._job.id);
      this.loaded();
      if (job?.status === 'COMPLETE' || confirmedComplete(answer)) {
        this._jobComplete();
        return;
      }
      if (job) {
        this._job = job;
        this.requestUpdate();
      }
      this.showError(confirmRefusal(answer) ?? "The top-up couldn't be confirmed yet. Please try again shortly.");
      await this.__readBalance(true);
    } catch (err) {
      this.loaded();
      this.showError(lambdaRefusal(err, "The top-up couldn't be confirmed. Please try again shortly."));
      await this.__readBalance(true);
    } finally {
      this.__submitting = false;
    }
  }

  render() {
    const figures = this.__figures;
    const { network } = figures;
    const reading = this.__reading(network);
    const accountId = reading?.accountId ?? null;
    const feesWaiting = feesWaitingLine(figures);
    const faucet = network ? ledgerFaucetUrl(network) : null;
    return html`
      <ns-job-layout>
        <p slot="intro">${platformTopUpIntro(network ? ledgerNetworkLabel(network) : null)}</p>
        <div class="job-panel">
          <h3>nodeStream's account</h3>
          ${feesWaiting
            ? html`
                <p role="status">${feesWaiting}</p>
              `
            : nothing}
          <ns-ledger-balance label="Balance" .reading=${reading} copyable></ns-ledger-balance>
          <ns-detail-list
            .rows=${[
              { label: 'Network', value: network ? ledgerNetworkLabel(network) : undefined },
              { label: 'Target', value: credits(figures.targetCredits) },
              { label: 'Amount to send', value: credits(figures.requestedCredits) },
            ]}
          ></ns-detail-list>
        </div>
        <div class="job-panel">
          <h3>Enter the new total</h3>
          <p>
            Once the funds have arrived, enter the account's new total balance in credits. The public ledger is checked
            before the job is completed.
          </p>
          <ns-input-field
            stacked
            required
            label="New total (credits)*"
            placeholder="Enter Here"
            .value=${this.__entered}
            @input=${(e: Event) => {
              this.__entered = (e.target as unknown as { value: string }).value ?? '';
              this.__inputError = undefined;
            }}
          ></ns-input-field>
          ${this.__inputError
            ? html`
                <div class="error-text" role="alert">${this.__inputError}</div>
              `
            : nothing}
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>How to top it up</h3>
          <ol>
            <li>
              Send at least ${credits(figures.requestedCredits) ?? 'the amount shown'} to the account, outside
              nodeStream. 100 credits are 1 unit of the public ledger's own currency.
            </li>
            <li>Check the account on the public ledger once the funds have arrived.</li>
            <li>Enter the account's new total here and press "I've topped it up".</li>
          </ol>
          ${accountId && network
            ? html`
                <p>
                  <a href=${ledgerExplorerUrl(network, 'account', accountId)} target="_blank" rel="noopener noreferrer">
                    View the account on the public ledger
                    <span class="visually-hidden">(opens in a new tab)</span>
                  </a>
                </p>
              `
            : nothing}
          ${faucet
            ? html`
                <p>
                  <a href=${faucet} target="_blank" rel="noopener noreferrer">
                    Get free testnet funds
                    <span class="visually-hidden">(opens in a new tab)</span>
                  </a>
                </p>
              `
            : nothing}
          <p class="note">
            This job stays open until the account has reached its target and nothing is owed to companies.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__submitting} @click=${() => this.__confirm()}>
        I've topped it up
      </wa-button>
    `;
  }
}
